/**
 * SoundManager — Global procedural sound system for Moonrock
 *
 * Singleton that uses the Web Audio API to generate game sounds entirely
 * from oscillators (no audio files required). Respects the user's audio
 * toggle stored in localStorage under "moonrock_audio_enabled".
 *
 * Volume categories:
 *   UI sounds      — 0.3
 *   Reward sounds  — 0.6
 *   Celebration    — 0.8
 */

const AUDIO_STORAGE_KEY = "moonrock_audio_enabled";

type SoundName =
  | "coin_collect"
  | "merge_pop"
  | "critical_merge"
  | "level_up"
  | "evolution"
  | "button_click"
  | "error_buzz"
  | "reward_claim"
  | "star_earn"
  | "correct"
  | "stardust"
  | "combo_charge"
  | "combo_boost";

class SoundManager {
  private static instance: SoundManager;
  private ctx: AudioContext | null = null;
  private enabled: boolean;

  private constructor() {
    this.enabled = this.loadPreference();
  }

  static getInstance(): SoundManager {
    if (!SoundManager.instance) {
      SoundManager.instance = new SoundManager();
    }
    return SoundManager.instance;
  }

  // ── Preference persistence ──────────────────────────────────────────

  private loadPreference(): boolean {
    try {
      const stored = localStorage.getItem(AUDIO_STORAGE_KEY);
      if (stored === null) return true; // default on
      return stored === "true";
    } catch {
      return true;
    }
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    try {
      localStorage.setItem(AUDIO_STORAGE_KEY, String(enabled));
    } catch {
      // localStorage may be unavailable in some contexts
    }
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  // ── Audio context (lazy, resumes on user gesture) ───────────────────

  private getContext(): AudioContext {
    if (!this.ctx) {
      this.ctx = new (window.AudioContext ||
        (window as any).webkitAudioContext)();
    }
    // Resume suspended context (autoplay policy)
    if (this.ctx.state === "suspended") {
      this.ctx.resume().catch(() => {});
    }
    return this.ctx;
  }

  // ── Public API ──────────────────────────────────────────────────────

  play(soundName: SoundName): void {
    if (!this.enabled) return;

    const generator = this.sounds[soundName];
    if (generator) {
      try {
        generator();
      } catch {
        // Swallow errors so sounds never break gameplay
      }
    }
  }

  // ── Sound registry ──────────────────────────────────────────────────

  private sounds: Record<SoundName, () => void> = {
    coin_collect: () => this.coinCollect(),
    merge_pop: () => this.mergePop(),
    critical_merge: () => this.criticalMerge(),
    level_up: () => this.levelUp(),
    evolution: () => this.evolution(),
    button_click: () => this.buttonClick(),
    error_buzz: () => this.errorBuzz(),
    reward_claim: () => this.rewardClaim(),
    star_earn: () => this.starEarn(),
    correct: () => this.correct(),
    stardust: () => this.stardust(),
    combo_charge: () => this.comboCharge(),
    combo_boost: () => this.comboBoost(),
  };

  /** A soft bell note with a quick attack and a long tail. */
  private bell(freq: number, at: number, volume: number, length = 0.45, type: OscillatorType = "sine"): void {
    const ctx = this.getContext();
    const t = ctx.currentTime + at;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, t);
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(volume, t + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + length);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(t);
    osc.stop(t + length + 0.02);
    // A quiet octave partial gives the bell its shimmer.
    const o2 = ctx.createOscillator();
    const g2 = ctx.createGain();
    o2.type = "sine";
    o2.frequency.setValueAtTime(freq * 2, t);
    g2.gain.setValueAtTime(0.0001, t);
    g2.gain.exponentialRampToValueAtTime(volume * 0.28, t + 0.01);
    g2.gain.exponentialRampToValueAtTime(0.0001, t + length * 0.6);
    o2.connect(g2);
    g2.connect(ctx.destination);
    o2.start(t);
    o2.stop(t + length);
  }

  /** Right answer: a rising major third, bright and short (reward 0.45). */
  private correct(): void {
    this.bell(659.25, 0, 0.4, 0.32);
    this.bell(830.61, 0.07, 0.42, 0.5);
  }

  /** Stardust landing in the Luna ring: two airy sparkles (reward 0.25). */
  private stardust(): void {
    this.bell(1567.98, 0, 0.18, 0.22);
    this.bell(2093.0, 0.05, 0.14, 0.3);
  }

  /** The Luna is charged: a rising three-note shimmer (reward 0.3). */
  private comboCharge(): void {
    this.bell(987.77, 0, 0.22, 0.25);
    this.bell(1318.51, 0.06, 0.24, 0.25);
    this.bell(1975.53, 0.12, 0.26, 0.45);
  }

  /** Doubled stardust: a bright chord with a sparkle on top (reward 0.4). */
  private comboBoost(): void {
    this.bell(1046.5, 0, 0.3, 0.5, "triangle");
    this.bell(1318.51, 0.02, 0.26, 0.5);
    this.bell(1567.98, 0.04, 0.24, 0.55);
    this.bell(2637.02, 0.12, 0.16, 0.4);
  }

  // ── Helpers ─────────────────────────────────────────────────────────

  /**
   * Create a simple oscillator → gain chain and return both nodes plus
   * the current time for scheduling.
   */
  private createOscillator(
    type: OscillatorType,
    frequency: number,
    volume: number
  ): { osc: OscillatorNode; gain: GainNode; now: number } {
    const ctx = this.getContext();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = type;
    osc.frequency.setValueAtTime(frequency, ctx.currentTime);
    gain.gain.setValueAtTime(volume, ctx.currentTime);

    osc.connect(gain);
    gain.connect(ctx.destination);

    return { osc, gain, now: ctx.currentTime };
  }

  // ── Sound generators ────────────────────────────────────────────────

  /** High blip — 800Hz to 1200Hz sweep, 100ms, triangle wave (reward 0.6) */
  private coinCollect(): void {
    const { osc, gain, now } = this.createOscillator("triangle", 800, 0.6);

    osc.frequency.exponentialRampToValueAtTime(1200, now + 0.1);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.1);

    osc.start(now);
    osc.stop(now + 0.1);
  }

  /** Mid pop — 400Hz to 200Hz, 150ms, sine wave with decay (UI 0.3) */
  private mergePop(): void {
    const { osc, gain, now } = this.createOscillator("sine", 400, 0.3);

    osc.frequency.exponentialRampToValueAtTime(200, now + 0.15);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.15);

    osc.start(now);
    osc.stop(now + 0.15);
  }

  /** Dramatic sweep — 200Hz to 800Hz, 300ms, sawtooth + reverb-like tail (celebration 0.8) */
  private criticalMerge(): void {
    const ctx = this.getContext();
    const { osc, gain, now } = this.createOscillator("sawtooth", 200, 0.8);

    osc.frequency.exponentialRampToValueAtTime(800, now + 0.3);

    // Simulate reverb with a secondary delayed oscillator
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = "sine";
    osc2.frequency.setValueAtTime(400, now);
    osc2.frequency.exponentialRampToValueAtTime(1000, now + 0.35);
    gain2.gain.setValueAtTime(0.25, now + 0.05);
    gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.5);
    osc2.connect(gain2);
    gain2.connect(ctx.destination);

    // Decay main
    gain.gain.setValueAtTime(0.8, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.35);

    osc.start(now);
    osc.stop(now + 0.35);
    osc2.start(now + 0.05);
    osc2.stop(now + 0.5);
  }

  /** Ascending arpeggio — C5 E5 G5 C6, 80ms each, sine wave (celebration 0.8) */
  private levelUp(): void {
    const ctx = this.getContext();
    const notes = [523.25, 659.25, 783.99, 1046.5]; // C5 E5 G5 C6

    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = "sine";
      osc.frequency.setValueAtTime(freq, ctx.currentTime);

      const startTime = ctx.currentTime + i * 0.08;
      gain.gain.setValueAtTime(0.001, startTime);
      gain.gain.linearRampToValueAtTime(0.8, startTime + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.001, startTime + 0.08);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(startTime);
      osc.stop(startTime + 0.08);
    });
  }

  /** Dramatic sweep + shimmer — low to high sweep with sparkle overlay (celebration 0.8) */
  private evolution(): void {
    const ctx = this.getContext();
    const now = ctx.currentTime;

    // Low-to-high sweep
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.type = "sawtooth";
    osc1.frequency.setValueAtTime(150, now);
    osc1.frequency.exponentialRampToValueAtTime(1200, now + 0.5);
    gain1.gain.setValueAtTime(0.6, now);
    gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.6);
    osc1.connect(gain1);
    gain1.connect(ctx.destination);
    osc1.start(now);
    osc1.stop(now + 0.6);

    // High-frequency sparkle shimmer
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = "sine";
    osc2.frequency.setValueAtTime(2400, now + 0.15);
    osc2.frequency.exponentialRampToValueAtTime(3200, now + 0.55);
    gain2.gain.setValueAtTime(0.001, now);
    gain2.gain.linearRampToValueAtTime(0.4, now + 0.2);
    gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.7);
    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    osc2.start(now + 0.15);
    osc2.stop(now + 0.7);

    // Extra twinkle layer
    const osc3 = ctx.createOscillator();
    const gain3 = ctx.createGain();
    osc3.type = "sine";
    osc3.frequency.setValueAtTime(3600, now + 0.3);
    osc3.frequency.exponentialRampToValueAtTime(4200, now + 0.6);
    gain3.gain.setValueAtTime(0.001, now + 0.3);
    gain3.gain.linearRampToValueAtTime(0.2, now + 0.4);
    gain3.gain.exponentialRampToValueAtTime(0.001, now + 0.8);
    osc3.connect(gain3);
    gain3.connect(ctx.destination);
    osc3.start(now + 0.3);
    osc3.stop(now + 0.8);
  }

  /** Subtle tick — 1000Hz, 30ms, square wave (UI 0.3) */
  private buttonClick(): void {
    const { osc, gain, now } = this.createOscillator("square", 1000, 0.3);

    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.03);

    osc.start(now);
    osc.stop(now + 0.03);
  }

  /** Low buzz — 150Hz, 200ms, sawtooth (UI 0.3) */
  private errorBuzz(): void {
    const { osc, gain, now } = this.createOscillator("sawtooth", 150, 0.3);

    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.2);

    osc.start(now);
    osc.stop(now + 0.2);
  }

  /** Cha-ching — two tones: 800Hz then 1200Hz, 100ms each (reward 0.6) */
  private rewardClaim(): void {
    const ctx = this.getContext();
    const now = ctx.currentTime;

    // First tone
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.type = "sine";
    osc1.frequency.setValueAtTime(800, now);
    gain1.gain.setValueAtTime(0.6, now);
    gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.1);
    osc1.connect(gain1);
    gain1.connect(ctx.destination);
    osc1.start(now);
    osc1.stop(now + 0.1);

    // Second tone
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = "sine";
    osc2.frequency.setValueAtTime(1200, now + 0.1);
    gain2.gain.setValueAtTime(0.6, now + 0.1);
    gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.2);
    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    osc2.start(now + 0.1);
    osc2.stop(now + 0.2);
  }

  /** Twinkle — 1400Hz to 1800Hz, 80ms, sine, gentle (reward 0.6) */
  private starEarn(): void {
    const { osc, gain, now } = this.createOscillator("sine", 1400, 0.6);

    osc.frequency.exponentialRampToValueAtTime(1800, now + 0.08);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.08);

    osc.start(now);
    osc.stop(now + 0.08);
  }
}

// ── Exports ─────────────────────────────────────────────────────────────

export const soundManager = SoundManager.getInstance();

/** React hook for accessing the sound manager singleton */
export const useSound = () => soundManager;

export type { SoundName };
export default SoundManager;
